import { Fragment, ReactElement, useContext, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import config from '../config';
import { SnippetScopeContext } from '../contexts/snippet-scope';
import { useSelectedDocumentationToken } from '../hooks/documentation-token';
import {
  BuiltInSnippetVars,
  SnippetDefinition,
  SnippetVariant,
  SnippetVarValues,
} from '../types';
import { renderSnippetLines } from '../utilities/snippet-render';
import { CodeViewer, CodeViewerVariant } from './CodeViewer';

// Indexed by the class numbers in generated snippets. The generator's list is
// in scripts/snippets/scope-rules.mjs; keep the two in step
export const SNIPPET_CLASS_NAMES = [
  'code-identifier',
  'code-keyword',
  'code-string',
  'code-number',
  'code-comment',
  'code-punctuation',
  'code-operator',
];

const NO_TOKEN = '<YOUR TOKEN>';

type SnippetVarsProp<V extends SnippetVarValues> = keyof V extends never
  ? { vars?: Partial<BuiltInSnippetVars> }
  : { vars: V & Partial<BuiltInSnippetVars> };

type SnippetFlagsProp<F extends string> = [F] extends [never]
  ? { flags?: undefined }
  : { flags: Record<F, boolean> };

export type SnippetProps<V extends SnippetVarValues, F extends string> = {
  snippet: SnippetDefinition<V, F>;
  id?: string;
  className?: string;
  border?: boolean;
  lineNumbers?: boolean;
  copyable?: boolean;
} & SnippetVarsProp<V> &
  SnippetFlagsProp<F>;

function renderVariant(
  variant: SnippetVariant,
  values: SnippetVarValues,
  flags: Record<string, boolean>,
  uuid: (key: string) => string
): CodeViewerVariant {
  const lines = renderSnippetLines(variant, values, flags, uuid);

  return {
    label: variant.label,
    lang: variant.lang,
    content: lines.map((pieces, i) => (
      <Fragment key={i}>
        {i > 0 && '\n'}
        {pieces.map(([className, text], j) =>
          className === null ? (
            text
          ) : (
            <span key={j} className={SNIPPET_CLASS_NAMES[className]}>
              {text}
            </span>
          )
        )}
      </Fragment>
    )),
    text: lines
      .map(pieces => pieces.map(([, text]) => text).join(''))
      .join('\n'),
    lineCount: lines.length,
  };
}

/**
 * Renders a generated snippet (see client/snippets/README.md) in a CodeViewer,
 * with its placeholders filled in.
 *
 * Built-in variables come from config and the documentation token selector,
 * then from the nearest <SnippetScope>, then from the vars prop
 */
export function Snippet<V extends SnippetVarValues, F extends string>({
  snippet,
  vars,
  flags,
  ...props
}: SnippetProps<V, F>): ReactElement {
  const scope = useContext(SnippetScopeContext);
  const selectedToken = useSelectedDocumentationToken();
  const [localUuids] = useState(() => new Map<string, string>());

  const variants = useMemo(() => {
    const values: SnippetVarValues = {
      apiUrl: config.api.url,
      token: selectedToken ?? NO_TOKEN,
      statsMaxDays: config.statsMaxDays,
      statsDefaultDays: config.statsDefaultDays,
      ...scope?.vars,
      ...vars,
    };

    const uuid =
      scope?.uuid ??
      ((key: string) => {
        if (!localUuids.has(key)) {
          localUuids.set(key, uuidv4());
        }
        return localUuids.get(key)!;
      });

    return snippet.variants.map(variant =>
      renderVariant(variant, values, flags ?? {}, uuid)
    );
  }, [snippet, vars, flags, scope, selectedToken, localUuids]);

  return <CodeViewer variants={variants} {...props} />;
}
